import React, { createContext, useCallback, useContext, useRef, useState } from 'react'
import {
  FormStepContext,
  TaskFormData,
  SubtaskActions,
  StepType,
} from 'components/TasksV2/TaskCreation/FormStepContext'
import { IAIGeneratedSubtask, AISubtaskType } from 'components/TasksV2/AITaskCreation/hooks/useAISubtaskGeneration'
import { useAiAuditGeneration } from './hooks/useAiAuditGeneration'
import { buildAiTaskDraft } from './model/subtaskDraft'
import { useAiTaskSubmission } from './hooks/useAiTaskSubmission'

export const AIStepType = {
  AI_ONBOARDING: 'AI_ONBOARDING',
  AI_PROMPT: 'AI_PROMPT',
  /** Prompt-writing helper conversation — a detour from AI_PROMPT, rendered inside the same modal. */
  AI_PROMPT_GENERATOR: 'AI_PROMPT_GENERATOR',
  AI_SUBTASK: 'AI_SUBTASK',
  AI_SETTINGS: 'AI_SETTINGS',
} as const

type AIStepType = typeof AIStepType[keyof typeof AIStepType]

export type { AISubtaskType }

// Loading context — tracks the full submission flow (createPrompt + createTask)
export interface IAILoadingContext {
  isSubmitting: boolean
  publicationComplete?: boolean
  isGenerating: boolean
  generationComplete?: boolean
  selectedSubtaskType: AISubtaskType | null
  setSelectedSubtaskType: (type: AISubtaskType) => void
  generateFromPrompt: (prompt: string, referenceImages: string[], scopeKey?: string) => Promise<void>
}

export const AILoadingContext = createContext<IAILoadingContext>({
  isSubmitting: false,
  isGenerating: false,
  selectedSubtaskType: null,
  setSelectedSubtaskType: () => {},
  generateFromPrompt: async () => {},
})

export const useAILoadingContext = () => useContext(AILoadingContext)

interface IAIFormStepProviderProps {
  children: React.ReactNode
  onClose?: VoidFunction
}

export const AIFormStepProvider: React.FC<IAIFormStepProviderProps> = ({ children, onClose }) => {
  const [currentFormSubmit, setCurrentFormSubmit] = useState<(() => void) | null>(null)
  const [formData, setFormData] = useState<Partial<TaskFormData>>({})
  const [stepStack, setStepStack] = useState<string[]>([AIStepType.AI_ONBOARDING])
  const [isMediaUploading, setIsMediaUploading] = useState(false)
  const [maxAllowedOverdueDays, setMaxAllowedOverdueDays] = useState<number>(8)
  const [subtaskActions, setSubtaskActions] = useState<SubtaskActions>({})
  const [selectedSubtaskType, _setSelectedSubtaskType] = useState<AISubtaskType | null>(null)
  const selectedSubtaskTypeRef = useRef<AISubtaskType>('image')

  const setSelectedSubtaskType = useCallback((type: AISubtaskType) => {
    _setSelectedSubtaskType(type)
    selectedSubtaskTypeRef.current = type
  }, [])

  const formDataRef = useRef<Partial<TaskFormData>>({})

  const updateFormData = useCallback(<T extends keyof TaskFormData>(step: T, data: TaskFormData[T]) => {
    formDataRef.current = { ...formDataRef.current, [step]: data }
    setFormData(formDataRef.current)
  }, [])

  const sumbitStep = useCallback(() => {
    currentFormSubmit?.()
  }, [currentFormSubmit])

  const goToPreviousStep = useCallback(() => {
    setStepStack((prev) => prev.slice(0, -1))
  }, [])

  // The whole task is generated from the prompt: LLM title → task title, desc → subtask description.
  const handleGenerated = useCallback(
    (result: IAIGeneratedSubtask, referenceImages: string[], subtaskType: AISubtaskType) => {
      updateFormData(StepType.SUBTASK_TASK_CREATION, buildAiTaskDraft(result, referenceImages, subtaskType))
    },
    [updateFormData]
  )

  const { isGenerating, generationComplete, generateFromPrompt } = useAiAuditGeneration({
    getSubtaskType: () => selectedSubtaskTypeRef.current,
    onGenerated: handleGenerated,
  })

  const getFormData = useCallback(() => formDataRef.current, [])
  const { isSubmitting, submitAllForms, publicationComplete } = useAiTaskSubmission(getFormData, onClose)

  return (
    <AILoadingContext.Provider
      value={{
        isSubmitting,
        publicationComplete,
        isGenerating,
        generationComplete,
        selectedSubtaskType,
        setSelectedSubtaskType,
        generateFromPrompt,
      }}
    >
      <FormStepContext.Provider
        value={{
          currentFormSubmit,
          setCurrentFormSubmit,
          formData,
          getFormData,
          updateFormData,
          stepStack,
          setStepStack,
          sumbitStep,
          goToPreviousStep,
          submitAllForms,
          subtaskActions,
          setSubtaskActions,
          isMediaUploading,
          setIsMediaUploading,
          isSubmitting,
          publicationComplete,
          isEdit: false,
          maxAllowedOverdueDays,
          setMaxAllowedOverdueDays,
          isVmTask: false,
          isAiTask: true,
        }}
      >
        {children}
      </FormStepContext.Provider>
    </AILoadingContext.Provider>
  )
}
